import { Navigate, Route, Routes } from "react-router-dom";

import { OrganisationPermissionConstants, ResourcePermissionConstants } from "../constants";
import {
  ActivityPageParams,
  PrivateActivityDocumentsParams,
  PrivateProjectDocumentsParams,
  PublicProjectOverviewPreviewParams,
} from "../models";
import {
  AssetManagerActivitiesPage,
  AssetManagerDashboardPage,
  AssetManagerDealDocumentDetailsPage,
  AssetManagerDealPage,
  AssetManagerDealWorkflowsPage,
  AssetManagerDeliveryWorkflowsPage,
  AssetManagerInvestmentDirectory,
  AssetManagerOperationsPage,
  AssetManagerPortfolioPage,
  AssetManagerProjectOverviewTab,
  AssetManagerSettingsPage,
  AssetManagerUsersEditPage,
  AssetManagerUsersListPage,
  AssetManagerUsersSendInvitationPage,
  AssetManagerWorkflowsPage,
  NewDealJourneyPage,
  NewDealJourneyProvider,
  PortfolioRoadmapPage,
  PortfolioTrackerPage,
  ProjectPage,
} from "../route/asset-manager";
import { Error403Page, Error404Page, Error500Page } from "../route/asset-manager/errors";
import { AssetManagerChangePasswordPage, AssetManagerChangePersonalDetailsPage } from "../route/asset-manager/profile";
import {
  AssetManagerProjectDetailsTab,
  AssetManagerProjectDocumentsTab,
  AssetManagerProjectInventoryTab,
} from "../route/asset-manager/projects/project";
import { AssetManagerOrganisationTab } from "../route/asset-manager/settings/organisation";
import { ActivityEditPage, ActivityViewPage, ActivityWizardProvider } from "../route/developer/activities";
import { PublicPage } from "../route/public";
import { PublicProjectOverviewPreviewPage, PublicProjectPreviewContainer } from "../route/public/project";
import {
  ActivityDashboardProvider,
  ActivityDiscussionProvider,
  ActivityDiscussionTab,
  ActivityPage,
  DocumentsTab as ActivityDocumentsTab,
  VersionHistoryTab,
} from "../route/shared/activities-dashboard";
import { ActivityDocumentHistoryPage } from "../route/shared/activities-dashboard/documents";
import { AssetManagerCompaniesListPage } from "../route/shared/companies";
import {
  LandholdingDetailsTab,
  LandholdingDocumentsTab,
  LandholdingInventoryTab,
  LandholdingLocationTab,
  LandholdingPage,
  LandholdingProvider,
  LandholdingTasksTab,
} from "../route/shared/landholding";
import {
  DocumentProvider,
  GroupProvider,
  LocationTab,
  MarketingSettingsTab,
  NewProjectJourneyPage,
  ProjectAccessAcceptInvitationPage,
  ProjectAccessDetailsPage,
  ProjectAccessInvitePage,
  ProjectAccessTab,
  ProjectActivitiesTab,
  ProjectAuditHistoryPage,
  ProjectDocumentHistoryPage,
  ProjectOverviewManageProjectGroupPage,
  ProjectProvider,
  SharedProjectsPage,
} from "../route/shared/projects";
import { TasksTab } from "../route/shared/projects/project";
import { ProjectRoadmapPage } from "../route/shared/projects/project-roadmap";
import { ProjectTrackerPage } from "../route/shared/projects/project-tracker";
import { AuditHistoryTab } from "../route/shared/settings/audit-history";
import { SettingsProjectAccessInvitePage, SettingsProjectAccessTab } from "../route/shared/settings/project-access";
import {
  TasksBoardPage,
  TasksBoardProvider,
  TasksDashboardPage,
  TasksDashboardProvider,
} from "../route/shared/workflows";
import { SharedWorkflowTemplatePage, WorkflowTemplateProvider } from "../route/shared/workflows/workflow-templates";
import { TagsProvider } from "../templates/TagsPanel/TagsContext";
import { useRouteGuards } from "./guards";

export const AssetManagerRoutes = (): JSX.Element => {
  const { RequireUserPermission, RequireAssetManagerAuth } = useRouteGuards();

  return (
    <div className="asset-manager-wrapper">
      <Routes>
        <Route
          path="dashboard"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerDashboardPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="investment-directory"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerInvestmentDirectory />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="portfolio/*"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerPortfolioPage />
            </RequireAssetManagerAuth>
          }
        >
          <Route
            path="pipeline-tracker"
            element={
              <RequireAssetManagerAuth>
                <PortfolioTrackerPage />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="pipeline-roadmap"
            element={
              <RequireAssetManagerAuth>
                <PortfolioRoadmapPage />
              </RequireAssetManagerAuth>
            }
          />
          <Route path="*" element={<Navigate to="pipeline-tracker" replace />} />
        </Route>
        <Route
          path="new-deal"
          element={
            <RequireAssetManagerAuth>
              <NewDealJourneyProvider>
                <NewDealJourneyPage />
              </NewDealJourneyProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="deal/:dealUuid/listing"
          element={
            <RequireAssetManagerAuth>
              <TagsProvider>
                <AssetManagerDealPage />
              </TagsProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="deal/:dealUuid/documents/:dealDocumentUuid"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerDealDocumentDetailsPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="portfolio/investment-directory"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerUsersSendInvitationPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="activities"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerActivitiesPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path={`activities/:${ActivityPageParams.activityHistoryUuid}/edit`}
          element={
            <RequireAssetManagerAuth>
              <ActivityWizardProvider>
                <ActivityEditPage />
              </ActivityWizardProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path={`activities/:${ActivityPageParams.activityHistoryUuid}/view`}
          element={
            <RequireAssetManagerAuth>
              <ActivityWizardProvider>
                <ActivityViewPage />
              </ActivityWizardProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path={`activities/:${ActivityPageParams.activityHistoryUuid}/view?:location`}
          element={
            <RequireAssetManagerAuth>
              <ActivityViewPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path={`activity/:${ActivityPageParams.activityUuid}/document/:${PrivateActivityDocumentsParams.activityDocumentUuid}`}
          element={
            <RequireAssetManagerAuth>
              <ActivityDashboardProvider>
                <ActivityDocumentHistoryPage />
              </ActivityDashboardProvider>
            </RequireAssetManagerAuth>
          }
        />

        <Route
          path={`activity/:${ActivityPageParams.activityUuid}/*`}
          element={
            <ActivityDashboardProvider>
              <ActivityPage />
            </ActivityDashboardProvider>
          }
        >
          <Route
            path="discussions"
            element={
              <ActivityWizardProvider>
                <RequireAssetManagerAuth>
                  <ActivityDiscussionProvider>
                    <ActivityDiscussionTab />
                  </ActivityDiscussionProvider>
                </RequireAssetManagerAuth>
              </ActivityWizardProvider>
            }
          />
          <Route
            path="documents"
            element={
              <RequireAssetManagerAuth>
                <ActivityDocumentsTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="history"
            element={
              <ActivityWizardProvider>
                <RequireAssetManagerAuth>
                  <VersionHistoryTab />
                </RequireAssetManagerAuth>
              </ActivityWizardProvider>
            }
          />
        </Route>
        <Route
          path="workflows/tasks-board/:workflowUuid"
          element={
            <RequireAssetManagerAuth>
              <TasksBoardProvider>
                <TasksBoardPage />
              </TasksBoardProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="operations/workflows/*"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerWorkflowsPage />
            </RequireAssetManagerAuth>
          }
        >
          <Route path="deal" element={<AssetManagerDealWorkflowsPage />} />
          <Route path="delivery" element={<AssetManagerDeliveryWorkflowsPage />} />
          <Route
            path="delivery/project-tasks-board/:workflowUuid"
            element={
              <TasksBoardProvider>
                <TasksBoardPage />
              </TasksBoardProvider>
            }
          />
        </Route>
        <Route
          path="workflows/templates/:workflowTemplateUuid"
          element={
            <WorkflowTemplateProvider>
              <RequireAssetManagerAuth>
                <SharedWorkflowTemplatePage />
              </RequireAssetManagerAuth>
            </WorkflowTemplateProvider>
          }
        />
        {/* Outside the `operations/*` shell below: the dashboard renders its own
            AssetManagerTemplate. A static segment still out-ranks that splat. */}
        <Route
          path="operations/tasks"
          element={
            <TasksDashboardProvider>
              <RequireAssetManagerAuth>
                <TasksDashboardPage />
              </RequireAssetManagerAuth>
            </TasksDashboardProvider>
          }
        />
        <Route
          path="operations/*"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerOperationsPage />
            </RequireAssetManagerAuth>
          }
        >
          <Route path="companies" element={<AssetManagerCompaniesListPage />} />
          <Route path="" element={<Navigate to="companies" replace />} />
        </Route>
        <Route
          path="projects/new-project"
          element={
            <RequireAssetManagerAuth>
              <RequireUserPermission userPermission={ResourcePermissionConstants.CREATE_RESOURCE}>
                <NewProjectJourneyPage />
              </RequireUserPermission>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="projects/*"
          element={
            <RequireAssetManagerAuth>
              <SharedProjectsPage />
            </RequireAssetManagerAuth>
          }
        >
          <Route
            path="project-tracker"
            element={
              <RequireAssetManagerAuth>
                <ProjectTrackerPage />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="project-roadmap"
            element={
              <RequireAssetManagerAuth>
                <ProjectRoadmapPage />
              </RequireAssetManagerAuth>
            }
          />
          <Route path="" element={<Navigate to="project-tracker" replace />} />
        </Route>
        <Route
          path="projects/:projectUuid/*"
          element={
            <ProjectProvider>
              <TagsProvider>
                <ProjectPage />
              </TagsProvider>
            </ProjectProvider>
          }
        >
          <Route
            path="overview"
            element={
              <RequireAssetManagerAuth>
                <AssetManagerProjectOverviewTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="location"
            element={
              <RequireAssetManagerAuth>
                <LocationTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="details"
            element={
              <RequireAssetManagerAuth>
                <AssetManagerProjectDetailsTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="inventory"
            element={
              <RequireAssetManagerAuth>
                <AssetManagerProjectInventoryTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="activities"
            element={
              <RequireAssetManagerAuth>
                <ProjectActivitiesTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="documents"
            element={
              <RequireAssetManagerAuth>
                <AssetManagerProjectDocumentsTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="marketing-settings"
            element={
              <RequireAssetManagerAuth>
                <MarketingSettingsTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="tasks"
            element={
              <RequireAssetManagerAuth>
                <TasksTab type="seed" />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="access"
            element={
              <RequireAssetManagerAuth>
                <ProjectAccessTab type="seed" />
              </RequireAssetManagerAuth>
            }
          />
        </Route>
        <Route
          path="projects/:projectUuid/access/invite"
          element={
            <RequireAssetManagerAuth>
              <ProjectProvider>
                <ProjectAccessInvitePage />
              </ProjectProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="projects/:projectUuid/access/invite/:projectOrganisationInvitationUuid"
          element={
            <RequireAssetManagerAuth>
              <ProjectProvider>
                <ProjectAccessDetailsPage />
              </ProjectProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="project/access/invite/accept/:projectOrganisationInvitationUuid"
          element={
            <RequireAssetManagerAuth>
              <ProjectProvider>
                <ProjectAccessAcceptInvitationPage />
              </ProjectProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path={`projects/:${PrivateProjectDocumentsParams.projectUuid}/documents/:${PrivateProjectDocumentsParams.projectDocumentUuid}`}
        >
          <Route
            path=""
            element={
              <RequireAssetManagerAuth>
                <ProjectProvider>
                  <DocumentProvider>
                    <ProjectDocumentHistoryPage />
                  </DocumentProvider>
                </ProjectProvider>
              </RequireAssetManagerAuth>
            }
          />
        </Route>
        <Route
          path="projects/:projectUuid/audit-history"
          element={
            <RequireAssetManagerAuth>
              <ProjectProvider>
                <ProjectAuditHistoryPage />
              </ProjectProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="landholding/:landholdingUuid/*"
          element={
            <LandholdingProvider>
              <TagsProvider>
                <LandholdingPage />
              </TagsProvider>
            </LandholdingProvider>
          }
        >
          <Route
            path="details"
            element={
              <RequireAssetManagerAuth>
                <LandholdingDetailsTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="location"
            element={
              <RequireAssetManagerAuth>
                <LandholdingLocationTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="inventory"
            element={
              <RequireAssetManagerAuth>
                <LandholdingInventoryTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="documents"
            element={
              <RequireAssetManagerAuth>
                <LandholdingDocumentsTab />
              </RequireAssetManagerAuth>
            }
          />
          <Route
            path="tasks"
            element={
              <RequireAssetManagerAuth>
                <LandholdingTasksTab />
              </RequireAssetManagerAuth>
            }
          />
        </Route>
        <Route
          path="landholding/:groupUuid/manage-landholding"
          element={
            <RequireAssetManagerAuth>
              <GroupProvider>
                <ProjectOverviewManageProjectGroupPage />
              </GroupProvider>
            </RequireAssetManagerAuth>
          }
        />
        <Route path="project/*" element={<PublicPage />}>
          <Route
            path={`:${PublicProjectOverviewPreviewParams.uuid}/preview/:${PublicProjectOverviewPreviewParams.previewUuid}`}
            element={
              <RequireAssetManagerAuth>
                <PublicProjectPreviewContainer />
              </RequireAssetManagerAuth>
            }
          >
            <Route path="overview" element={<PublicProjectOverviewPreviewPage />} />
          </Route>
        </Route>
        <Route
          path="settings/*"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerSettingsPage />
            </RequireAssetManagerAuth>
          }
        >
          <Route
            path="users"
            element={
              <RequireUserPermission
                userPermission={OrganisationPermissionConstants.READ_ORGANISATION_USERS}
                redirectRoute="/am/settings/organisation"
              >
                <AssetManagerUsersListPage />
              </RequireUserPermission>
            }
          />
          <Route path="organisation" element={<AssetManagerOrganisationTab />} />
          <Route path="project-access" element={<SettingsProjectAccessTab />} />
          <Route path="audit-history" element={<AuditHistoryTab />} />
          <Route path="" element={<Navigate to="users" replace />} />
        </Route>
        <Route
          path="settings/project-access/invite"
          element={
            <RequireAssetManagerAuth>
              <SettingsProjectAccessInvitePage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="settings/users/send-invitation"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerUsersSendInvitationPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="settings/users/:userUuid/edit"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerUsersEditPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="profile/change-password"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerChangePasswordPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="profile/change-personal-details"
          element={
            <RequireAssetManagerAuth>
              <AssetManagerChangePersonalDetailsPage />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="403/access-denied"
          element={
            <RequireAssetManagerAuth>
              <Error403Page />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="500/application-error"
          element={
            <RequireAssetManagerAuth>
              <Error500Page />
            </RequireAssetManagerAuth>
          }
        />
        <Route
          path="*"
          element={
            <RequireAssetManagerAuth>
              <Error404Page />
            </RequireAssetManagerAuth>
          }
        />
      </Routes>
    </div>
  );
};
